import { Divider, Typography, useTheme } from "@mui/material";
import { type FC, useState } from "react";
import { ActivityItem } from "web_myplann/Activities/Components/ActivityItem";
import { TaskItem } from "web_myplann/Activities/Components/TaskItem";
import { useAssignedAgentsQuery } from "web_myplann/Api/Advisor/useAssignedAgentsQuery";
import {
	type ActivityListItemResponseDTO,
	type TaskListItemResponseDTO,
	UserAgentStatusEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { ToggleAll } from "web_myplann/Portfolio/Components/ToggleAll";

const MAX_SIZE = 999;
const DEFAULT_NORMAL_SIZE = 4;

type Props = {
	label: string;
	data: (ActivityListItemResponseDTO | TaskListItemResponseDTO)[];
	defaultNormalSize?: number;
	emptyGroupText?: string;
	noDivider?: boolean;
};

export const ActivityListGroup: FC<Props> = ({
	label,
	data,
	defaultNormalSize,
	emptyGroupText,
	noDivider,
}) => {
	const theme = useTheme();
	const { data: assignedAgents } = useAssignedAgentsQuery(
		UserAgentStatusEnum.Connected,
	);
	const hasMoreConnectedAgents = Number(assignedAgents?.data.length) > 1;

	const normalSize = defaultNormalSize ?? DEFAULT_NORMAL_SIZE;
	const [size, setSize] = useState<number>(normalSize);

	const hideSection = data.length === 0 && !emptyGroupText;
	const diplayEmptyText = data.length === 0 && emptyGroupText;

	const isActivityItem = (
		val: ActivityListItemResponseDTO | TaskListItemResponseDTO,
	): val is ActivityListItemResponseDTO => "type" in val || "timeFrom" in val;

	if (hideSection) {
		return null;
	}

	return (
		<>
			<Typography
				variant="deprecatedH3"
				color={theme.palette.deprecated.grey[700]}
			>
				{label}
			</Typography>

			{diplayEmptyText && (
				<Typography
					variant="deprecatedBody1"
					color={theme.palette.deprecated.grey[700]}
				>
					{emptyGroupText}
				</Typography>
			)}

			{data
				.slice(0, size)
				.map((item) =>
					isActivityItem(item) ? (
						<ActivityItem
							key={item.id}
							data={item}
							hasMoreConnectedAgents={hasMoreConnectedAgents}
						/>
					) : (
						<TaskItem
							key={item.id}
							data={item}
							hasMoreConnectedAgents={hasMoreConnectedAgents}
						/>
					),
				)}

			{data.length > normalSize && (
				<ToggleAll
					all={size === MAX_SIZE}
					onClick={() =>
						setSize((prev) => (prev === MAX_SIZE ? normalSize : MAX_SIZE))
					}
				/>
			)}

			{!noDivider && (
				<Divider
					sx={{
						borderWidth: 1,
						borderColor: theme.palette.deprecated.grey[100],
					}}
				/>
			)}
		</>
	);
};
